import React, { useState, useEffect } from 'react';
import { useNavigate, Link, Outlet, useLocation } from 'react-router-dom';
import { Layout, Menu, theme, Grid, Button, MenuProps } from 'antd';
import { useTranslation } from 'react-i18next';

// services
import authService from 'core/services/auth';
import { getDefaultLanguage, onChangeLanguage } from 'core/services/language';

// models
import { Admin } from 'modules/admins/models/Admin';
import { Language } from 'core/models/common';

//components
import Icon from 'utils/icon';

// styles
import defaultAvatar from 'assets/images/defaultAvatar.webp';
import logo from 'assets/images/logo.svg';
import './layout.scss';
import { useDispatch } from 'react-redux';
import { changeLanguage } from 'stores/useLanguageStore';

const { useBreakpoint } = Grid;

function DashboardLayout() {
	const screens = useBreakpoint();
	const navigate = useNavigate();
	const location = useLocation();
	const { t } = useTranslation();
	const dispatch = useDispatch();

	const [admin, setAdmin] = useState<Admin | null>();
	const [current, setCurrent] = useState('splash-screen');
	const [collapsed, setCollapsed] = useState(true);
	const [currentLanguage, setCurrentLanguage] = useState<Language>();
	const {
		token: { colorBgContainer },
	} = theme.useToken();

	const items = [
		{
			module: 'user',
			key: 'users',
			label: t('SIDEBAR.CLIENTS'),
			icon: (
				<span>
					<Icon name="users" />
				</span>
			),
		},
		{
			module: 'admin_manage',
			key: 'admin_manage',
			label: t('SIDEBAR.ADMIN_MANAGE'),
			icon: (
				<span>
					<Icon name="admin_manage" />
				</span>
			),
			children: [
				{
					module: 'admin',
					key: 'admins',
					label: t('SIDEBAR.ADMINS'),
					icon: (
						<span>
							<Icon name="admins" />
						</span>
					),
				},
				{
					module: 'role',
					key: 'roles',
					label: t('SIDEBAR.ROLES'),
					icon: (
						<span>
							<Icon name="roles" />
						</span>
					),
				},
			],
		},
		{
			module: 'geographic',
			key: 'geographic',
			label:t('SIDEBAR.GEOGRAPHIC'),
			icon: (
				<span>
					<Icon name="geographic" />
				</span>
			),
			children: [
				{
					module: 'country',
					key: 'countries',
					label: t('SIDEBAR.COUNTRIES'),
					icon: (
						<span>
							<Icon name="countries" />
						</span>
					),
				},
				{
					module: 'region',
					key: 'regions',
					label: t('SIDEBAR.REGIONS'),
					icon: (
						<span>
							<Icon name="regions" />
						</span>
					),
				},
				{
					module: 'city',
					key: 'cities',
					label: t('SIDEBAR.CITIES'),
					icon: (
						<span>
							<Icon name="cities" />
						</span>
					),
				},
				{
					module: 'district',
					key: 'districts',
					label: t('SIDEBAR.DISTRICTS'),
					icon: (
						<span>
							<Icon name="districts" />
						</span>
					),
				},
			],
		},
		{
			module: 'finance',
			key: 'finance',
			label: t('SIDEBAR.FINANCE'),
			icon: (
				<span>
					<Icon name="money" />
				</span>
			),
			children: [
				{
					module: 'payment',
					key: 'subscriptions',
					label: t('SIDEBAR.SUBSCRIPTIONS'),
					icon: (
						<span>
							<Icon name="subscription" />
						</span>
					),
				},
				{
					module: 'currency',
					key: 'currencies',
					label: t('SIDEBAR.CURRENCIES'),
					icon: (
						<span>
							<Icon name="currencies" />
						</span>
					),
				},
				{
					module: 'bank',
					key: 'banks',
					label: t('SIDEBAR.BANKS'),
					icon: (
						<span>
							<Icon name="banks" />
						</span>
					),
				},
			],
		},
		{
			module: 'language',
			key: 'languages',
			label: t('SIDEBAR.LANGUAGES'),
			icon: (
				<span>
					<Icon name="languages" />
				</span>
			),
		},
		{
			module: 'setting',
			key: 'settings',
			label: t('SIDEBAR.SETTINGS'),
			icon: (
				<span>
					<Icon name="settings2" />
				</span>
			),
		},
		{
			module: 'minors',
			key: 'minors',
			label: t('SIDEBAR.MINORS'),
			icon: (
				<span>
					<Icon name="minors" />
				</span>
			),
		},
		{
			module: 'incompetents',
			key: 'incompetents',
			label: t('SIDEBAR.INCOMPETENTS'),
			icon: (
				<span>
					<Icon name="incompetents" />
				</span>
			),
		},
		{
			module: 'commercial-entities',
			key: 'commercial-entities',
			label: t('SIDEBAR.COMMERCIAL_ENTITIES'),
			icon: (
				<span>
					<Icon name="commercial_entities" />
				</span>
			),
		}
	];

	const isSuperAdmin = authService.getUser()?.roles.findIndex((el) => el.name == 'super_admin')! >= 0;

	const filteredItems = isSuperAdmin ? items : items.filter((el) => authService.isPermittedToModule(el.module));

	const items2 = [
		{
			key: 'dashboard',
			label: t('SIDEBAR.DASHBOARD'),
			icon: (
				<span>
					<Icon name="dashboard" />
				</span>
			),
			danger: true,
		},
		{
			key: 'orders',
			label: t('SIDEBAR.ORDERS'),
			icon: (
				<span>
					<Icon name="orders" />
				</span>
			),
			danger: true,
		},
	];

	useEffect(() => {
		// set default language
		setCurrentLanguage(getDefaultLanguage());

		// navigate to first available module
		if (location.pathname === '/' && filteredItems.length > 0) {
			const firstModule = filteredItems[0].hasOwnProperty('children') ? (filteredItems[0] as any)?.children[0].key : filteredItems[0].key;
			setCurrent(firstModule);
			navigate(firstModule);
		} else if (location.pathname === '/' && filteredItems.length === 0) {
			navigate('not-authorized');
		} else setCurrent(location.pathname.split('/')[1]);
	}, []);

	// styles
	const siderStyle: React.CSSProperties = {
		overflow: 'hidden',
		position: 'fixed',
		insetInlineStart: 0,
		top: screens.sm ? 0 : 64,
		bottom: 0,
		scrollbarWidth: 'thin',
		background: '#fff',
		zIndex: 1000,
	};

	const headerStyle: React.CSSProperties = {
		padding: 0,
		background: colorBgContainer,
		position: 'sticky',
		top: 0,
		borderBottom: '1px solid #eee',
	};

	// handlers
	const onChangeCollapsible = () => {
		setCollapsed(!collapsed);
	};

	const onMenuClick: MenuProps['onClick'] = (e: any) => {
		if (window.innerWidth <= 576) setCollapsed(true);
		setCurrent(e.key);
		navigate(e.key);
	};

	const changeLanguageHandler = () => {
		onChangeLanguage();
		dispatch(changeLanguage());

		setCurrentLanguage(currentLanguage === 'ar' ? 'en' : 'ar');
	};

	const logoutHandler = () => {
		authService.clearAuthData();
		navigate('/login');
	};

	return (
		<Layout hasSider style={{ minHeight: '100vh' }}>
			<Layout.Sider
				style={siderStyle}
				breakpoint="lg"
				collapsedWidth={screens.md && !collapsed ? 250 : screens.sm ? 70 : 0}
				width={250}
				collapsed={collapsed}
				onCollapse={(value) => setCollapsed(value)}>
				<img
					src={logo}
					alt="logo"
					style={{
						width: 140,
						margin: 10,
						transform: `translateX(${
							screens.md && !collapsed
								? 50
								: screens.sm && currentLanguage === 'en'
								? '-98px'
								: screens.sm && currentLanguage === 'ar'
								? '-10px'
								: 0
						})`,
					}}
				/>

				<div style={{ overflow: 'auto', height: 'calc(100vh - 90px)' }}>
					<Menu items={filteredItems} onClick={onMenuClick} mode="inline" selectedKeys={[current]} />
					{/* <hr /> */}
					{/* <Menu items={items2} onClick={onMenuClick} selectedKeys={[current]} /> */}
				</div>
			</Layout.Sider>

			<Layout style={{ backgroundColor: '#fff', marginInlineStart: screens.md && !collapsed ? 250 : screens.sm ? 70 : 0 }}>
				<Layout.Header style={headerStyle} className="navHeader">
					<Button type="text" className={`menuToggler ${collapsed ? null : 'active'}`} onClick={onChangeCollapsible}>
						<span></span>
						<span></span>
						<span></span>
					</Button>

					<div>
						<Button type="text" className="language" onClick={changeLanguageHandler}>
							{currentLanguage === 'ar' ? 'English' : 'العربية'}
						</Button>
						<Button type="text">
							<Icon name="notification" width={35} height={35} />
						</Button>
						<Button type="text" className="profile">
							<Link to="/profile/data">
								<img alt="user" src={admin?.avatar_url ? admin.avatar_url : defaultAvatar} />
							</Link>
						</Button>
						<Button type="text" onClick={logoutHandler}>
							<Icon name="logout" width={35} height={35} />
						</Button>
					</div>
				</Layout.Header>

				<Layout.Content style={{ margin: '24px 16px 0', overflow: 'initial' }}>
					<Outlet />
				</Layout.Content>
			</Layout>
		</Layout>
	);
}

export default DashboardLayout;
